/**
 * CommandPalette Component
 * Global search accessible via Cmd+K / Ctrl+K
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  FolderKanban, 
  ArrowRight, 
  Settings,
  Home,
  Plus
} from 'lucide-react';
import { projectsApi } from '../../lib/api';

interface Project {
  id: string;
  code: string;
  name: string;
  line?: string;
  status: string;
  photoCount: number;
  anomalyCount: number;
}

interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  category: 'navigation' | 'project' | 'action';
}

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Load projects when opening
  useEffect(() => {
    if (isOpen) {
      loadProjects();
      inputRef.current?.focus();
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsApi.list({ limit: 100 });
      const projectList = response.data?.data || [];
      setProjects(projectList);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Static commands
  const staticCommands: CommandItem[] = useMemo(() => [
    {
      id: 'nav-dashboard',
      title: 'Go to Dashboard',
      icon: Home,
      action: () => navigate('/'),
      category: 'navigation',
    },
    {
      id: 'nav-projects',
      title: 'Go to Projects',
      icon: FolderKanban,
      action: () => navigate('/projects'),
      category: 'navigation',
    },
    {
      id: 'nav-settings',
      title: 'Go to Settings',
      icon: Settings,
      action: () => navigate('/settings'),
      category: 'navigation',
    },
    {
      id: 'action-new-project',
      title: 'Create New Project',
      icon: Plus,
      action: () => {
        navigate('/projects');
        // Trigger new project modal - would need state management
      },
      category: 'action',
    },
  ], [navigate]);

  // Project commands
  const projectCommands: CommandItem[] = useMemo(() => {
    return projects.map(project => ({
      id: `project-${project.id}`,
      title: project.code,
      subtitle: `${project.name}${project.line ? ` • ${project.line}` : ''} • ${project.photoCount} photos • ${project.anomalyCount} anomalies`,
      icon: FolderKanban,
      action: () => navigate(`/projects/${project.id}`),
      category: 'project' as const,
    }));
  }, [projects, navigate]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    const allCommands = [...staticCommands, ...projectCommands];
    
    if (!query.trim()) {
      return allCommands;
    }
    
    const lowerQuery = query.toLowerCase();
    return allCommands.filter(cmd => 
      cmd.title.toLowerCase().includes(lowerQuery) ||
      cmd.subtitle?.toLowerCase().includes(lowerQuery)
    );
  }, [staticCommands, projectCommands, query]);

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle navigation within the list
  const handleKeyNavigation = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = filteredCommands[selectedIndex];
      if (selected) {
        selected.action();
        setIsOpen(false);
      }
    }
  }, [filteredCommands, selectedIndex]);

  // Reset selected index when filtering
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />

      {/* Command Palette */}
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Search Input */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 mr-3" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyNavigation}
            placeholder="Search projects, navigate..."
            className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 outline-none text-base"
            autoComplete="off"
          />
          <div className="flex items-center gap-1 text-xs text-gray-400 ml-2">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium">ESC</kbd>
            <span>to close</span>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto py-2">
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-400">
              <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full mx-auto mb-2" />
              Loading...
            </div>
          ) : filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400">
              No results found
            </div>
          ) : (
            <>
              {/* Group by category */}
              {['navigation', 'action', 'project'].map(category => {
                const items = filteredCommands.filter(c => c.category === category);
                if (items.length === 0) return null;
                
                const categoryLabel = {
                  navigation: 'Navigation',
                  action: 'Actions',
                  project: 'Projects',
                }[category];
                
                return (
                  <div key={category}>
                    <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase">
                      {categoryLabel}
                    </div>
                    {items.map((item) => {
                      const globalIndex = filteredCommands.indexOf(item);
                      const Icon = item.icon;
                      
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            item.action();
                            setIsOpen(false);
                          }}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                            globalIndex === selectedIndex
                              ? 'bg-primary/10 text-primary'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className={`w-4 h-4 flex-shrink-0 ${
                            globalIndex === selectedIndex ? 'text-primary' : 'text-gray-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{item.title}</div>
                            {item.subtitle && (
                              <div className="text-xs text-gray-500 truncate">{item.subtitle}</div>
                            )}
                          </div>
                          {globalIndex === selectedIndex && (
                            <ArrowRight className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↑</kbd>
            <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px]">Enter</kbd>
            select
          </span>
        </div>
      </div>
    </div>
  );
}
